import { normalizeLang } from "./i18n";
import { getBlueprint, getAllBlueprintKeys } from "./BlueprintRegistry";

const getLabel = (node, lang, fallback = "") => {
  if (!node) return fallback;
  if (typeof node === "string") return node;
  return node[lang] || node.he || node.en || node.es || fallback;
};

function buildDaySubtitlesTriLang(bpHe, bpEn, bpEs) {
  if (!bpHe?.days?.length) return {};
  return bpHe.days.reduce((acc, day) => {
    const n = day.dayNumber;
    const dEn = bpEn?.days?.find((d) => d.dayNumber === n);
    const dEs = bpEs?.days?.find((d) => d.dayNumber === n);
    acc[n] = {
      he: getLabel(day.title, "he"),
      en: getLabel(dEn?.title, "en"),
      es: getLabel(dEs?.title, "es"),
    };
    return acc;
  }, {});
}

function buildMetaFromBlueprint(key) {
  const bpHe = getBlueprint(key, "he");
  const bpEn = getBlueprint(key, "en");
  const bpEs = getBlueprint(key, "es");
  if (!bpHe) return null;
  const totalDays =
    bpHe.totalDays || bpHe.durationDays || bpHe.days?.length || 14;
  const id = bpHe.id || `${key}_${totalDays}d`;
  const journeyName = bpHe.journeyName;
  const titles = {
    he: getLabel(journeyName, "he") || id,
    en: getLabel(journeyName, "en") || id,
    es: getLabel(journeyName, "es") || id,
  };
  const isTriLocale = bpHe !== bpEn || bpHe !== bpEs;
  const daySubtitles = isTriLocale
    ? buildDaySubtitlesTriLang(bpHe, bpEn, bpEs)
    : (bpHe.days?.reduce(
        (acc, day) => {
          acc[day.dayNumber] = {
            he: getLabel(day.title, "he"),
            en: getLabel(day.title, "en"),
            es: getLabel(day.title, "es"),
          };
          return acc;
        },
        {}
      ) || {});
  return { id, totalDays, titles, daySubtitles };
}

function buildJourneyBlueprints() {
  const keys = getAllBlueprintKeys();
  const phase1Keys = [
    "rel_breakup_fresh_pain",
    "rel_breakup_fresh_pain_phase1",
    "rel_breakup_fresh_pain_reference_18d",
  ];
  const allKeys = [...new Set([...keys, ...phase1Keys])];
  const out = {};
  for (const key of allKeys) {
    const meta = buildMetaFromBlueprint(key);
    if (meta) out[key] = meta;
  }
  return out;
}

export const JOURNEY_BLUEPRINTS = buildJourneyBlueprints();
const JOURNEY_BLUEPRINT_ALIASES = {
  cruel_self_judgment: "harsh_self_criticism",
  need_to_be_perfect_in_everything: "perfectionism_fear_of_mistakes_procrastination",
  fear_of_mistakes: "perfectionism_fear_of_mistakes_procrastination",
};

export function getBlueprintForProblem(problemKey, lang) {
  const resolvedKey = JOURNEY_BLUEPRINT_ALIASES[problemKey] || problemKey;
  const bp = JOURNEY_BLUEPRINTS[resolvedKey];
  if (!bp) return null;
  const safeLang = normalizeLang(lang);
  const rawTitle = bp.titles[safeLang] || bp.titles.he || "";
  const cleanTitle = String(rawTitle)
    .replace(/\s*(?:—|-)\s*\d+\s*(?:ימים|days?|d[ií]as?)\s*$/i, "")
    .trim();
  const days = Number(bp.totalDays) || 0;
  const unit =
    safeLang === "en" ? (days === 1 ? "day" : "days") :
    safeLang === "es" ? (days === 1 ? "día" : "días") :
    (days === 1 ? "יום" : "ימים");
  return {
    ...bp,
    title: days ? `${cleanTitle} — ${days} ${unit}` : cleanTitle,
    daySubtitles: bp.daySubtitles,
  };
}
